<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import AddMatchingEmailForm from '@/components/dashboard/matching/AddMatchingEmailForm.vue'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'
import { useEmailAccountActions } from '@/composables/useEmailAccountActions'
import { useRequestEmailVerification } from '@/composables/useRequestEmailVerification'

withDefaults(
  defineProps<{
    embedded?: boolean
  }>(),
  {
    embedded: false,
  }
)

const store = useStore()
const { setPrimaryEmail, promptDeleteEmail } = useEmailAccountActions()
const { resendVerification, cancelVerification } = useRequestEmailVerification()

const primaryEmail = computed(
  () => store.state.donor?.userDetails?.emailAddress as string | undefined
)

const additionalEmails = computed(() => {
  const accounts = store.state.accounts?.emailAccounts ?? []
  return accounts.filter((account: { isPrimary?: boolean; id: string }) => {
    if (account.isPrimary) return false
    if (primaryEmail.value && account.id === primaryEmail.value) return false
    return true
  })
})

const pendingEmails = computed(
  () =>
    (store.state.accounts?.pendingEmailVerifications ?? []) as Array<{
      email: string
      requestedAt: string
    }>
)

const settingPrimaryEmail = ref<string | null>(null)
const resendingEmail = ref<string | null>(null)
const cancellingEmail = ref<string | null>(null)
/** Brief highlight on the primary email row after a successful change. */
const primaryJustUpdated = ref(false)
let primaryHighlightTimer: ReturnType<typeof setTimeout> | null = null

const onSetPrimary = async (email: string) => {
  if (email === primaryEmail.value || settingPrimaryEmail.value) return
  settingPrimaryEmail.value = email
  try {
    const updated = await setPrimaryEmail(email)
    if (!updated) return

    primaryJustUpdated.value = false
    // Retrigger the CSS animation if the donor sets primary again quickly.
    requestAnimationFrame(() => {
      primaryJustUpdated.value = true
    })
    if (primaryHighlightTimer) clearTimeout(primaryHighlightTimer)
    primaryHighlightTimer = setTimeout(() => {
      primaryJustUpdated.value = false
      primaryHighlightTimer = null
    }, 1200)
  } finally {
    settingPrimaryEmail.value = null
  }
}

const onResendPending = async (email: string) => {
  if (resendingEmail.value) return
  resendingEmail.value = email
  try {
    await resendVerification(email)
  } finally {
    resendingEmail.value = null
  }
}

const onCancelPending = async (email: string) => {
  if (cancellingEmail.value) return
  cancellingEmail.value = email
  try {
    await cancelVerification(email)
  } finally {
    cancellingEmail.value = null
  }
}

onUnmounted(() => {
  if (primaryHighlightTimer) clearTimeout(primaryHighlightTimer)
})
</script>

<template>
  <component
    :is="embedded ? 'div' : 'section'"
    :class="embedded ? 'sa-matching-emails-panel' : 'sa-acct-section sa-matching-emails'"
    :aria-labelledby="embedded ? undefined : 'matching-emails-heading'"
  >
    <h2 v-if="!embedded" id="matching-emails-heading">
      {{ $t('Dashboard.Status.Emails.Heading') }}
    </h2>
    <p class="sa-matching-emails__lead">{{ $t('Dashboard.Status.Emails.Lead') }}</p>

    <div
      v-if="primaryEmail"
      class="sa-card-chip sa-card-chip--email sa-card-chip--email-primary"
      :class="{ 'sa-card-chip--email-primary-updated': primaryJustUpdated }"
    >
      <div class="sa-card-chip__icon sa-card-chip__icon--primary" aria-hidden="true">
        <DashboardNavIcon name="email" />
      </div>
      <div class="sa-card-chip__body sa-card-chip__body--grow">
        <div class="sa-detail-value">{{ primaryEmail }}</div>
        <div class="sa-card-chip__sub">{{ $t('Dashboard.Status.Emails.PrimarySub') }}</div>
      </div>
      <span class="sa-tag-active sa-card-chip__tag">
        {{ $t('Dashboard.Status.Emails.Primary') }}
      </span>
    </div>

    <div
      v-for="pending in pendingEmails"
      :key="`pending-${pending.email}`"
      class="sa-card-chip sa-card-chip--email sa-card-chip--email-pending"
    >
      <div class="sa-card-chip__icon sa-card-chip__icon--additional" aria-hidden="true">
        <DashboardNavIcon name="email" />
      </div>
      <div class="sa-card-chip__body sa-card-chip__body--grow">
        <div class="sa-detail-value">{{ pending.email }}</div>
        <div class="sa-card-chip__sub">
          {{ $t('Dashboard.Status.Emails.Pending.Sub') }}
        </div>
      </div>
      <span class="sa-tag-pending sa-card-chip__tag">
        {{ $t('Dashboard.Status.Emails.Pending.Badge') }}
      </span>
      <div class="sa-card-chip__actions sa-matching-emails__actions sa-matching-emails__actions--pending">
        <button
          type="button"
          class="sa-matching-emails__text-link sa-matching-emails__action-btn"
          :disabled="Boolean(resendingEmail)"
          :aria-label="
            $t('Dashboard.Status.Emails.Pending.ResendAria', { email: pending.email })
          "
          @click="onResendPending(pending.email)"
        >
          <span class="sa-matching-emails__label-short">
            {{ $t('Dashboard.Status.Emails.Pending.ResendShort') }}
          </span>
          <span class="sa-matching-emails__label-full">
            {{ $t('Dashboard.Status.Emails.Pending.Resend') }}
          </span>
        </button>
        <button
          type="button"
          class="sa-matching-emails__text-link sa-matching-emails__text-link--danger sa-matching-emails__action-btn"
          :disabled="Boolean(cancellingEmail)"
          :aria-label="
            $t('Dashboard.Status.Emails.Pending.CancelAria', { email: pending.email })
          "
          @click="onCancelPending(pending.email)"
        >
          <span class="sa-matching-emails__label-short">
            {{ $t('Dashboard.Status.Emails.Pending.CancelShort') }}
          </span>
          <span class="sa-matching-emails__label-full">
            {{ $t('Dashboard.Status.Emails.Pending.Cancel') }}
          </span>
        </button>
      </div>
    </div>

    <div
      v-for="account in additionalEmails"
      :key="account.id"
      class="sa-card-chip sa-card-chip--email sa-card-chip--email-additional"
    >
      <div class="sa-card-chip__icon sa-card-chip__icon--additional" aria-hidden="true">
        <DashboardNavIcon name="email" />
      </div>
      <div class="sa-card-chip__body sa-card-chip__body--grow">
        <div class="sa-detail-value">{{ account.id }}</div>
      </div>
      <div class="sa-card-chip__actions sa-matching-emails__actions">
        <button
          type="button"
          class="sa-matching-emails__text-link"
          :disabled="Boolean(settingPrimaryEmail)"
          :aria-label="
            $t('Dashboard.Status.Emails.SetPrimaryAria', { email: account.id })
          "
          @click="onSetPrimary(account.id)"
        >
          {{ $t('Dashboard.Status.Emails.SetPrimary') }}
        </button>
        <button
          type="button"
          class="sa-matching-emails__text-link sa-matching-emails__text-link--danger"
          :aria-label="$t('Dashboard.Status.Emails.DeleteAria', { email: account.id })"
          @click="promptDeleteEmail(account.id)"
        >
          {{ $t('Dashboard.Status.Emails.Delete') }}
        </button>
      </div>
    </div>

    <AddMatchingEmailForm />
  </component>
</template>
